import { keys, shuffle, sortBy, startCase, uniqBy, zip } from 'lodash'
import { SelectionRange, Shape } from 'plotly.js'
import { ColumnTable } from 'arquero'
import {
  CellDataField,
  CellInfoGroup,
  CellInfoGroupNone,
  CellInfoGroups,
  CellPlotDatum,
  PlotSelectionState,
  PinnedMorphotype,
  getDataFields,
} from 'redux/slices'
import { FeatureGroup } from 'utils/constants'

import area from '@turf/area'
import * as turf from '@turf/helpers'
import unkinkPolygon from '@turf/unkink-polygon'
import booleanPointInPolygon from '@turf/boolean-point-in-polygon'
import polygonClipping, { type Polygon as Poly } from 'polygon-clipping'
import { getColumn } from './arrow/arqueroHelpers'
import { CATEGORICAL_COLOR_PALETTE } from './shared'
import { CellInfo } from './tsv/types'

const { intersection } = polygonClipping
const COLORS = CATEGORICAL_COLOR_PALETTE

// The backend names custom run columns CATEGORY_<index>_<name>, where the index is the column's
// position in the sorted list of custom columns -- so it reaches double digits once a run has more
// than ten of them. Matching a single digit silently hid every column from CATEGORY_10_ onwards.
const isCategoryField = /^CATEGORY_[0-9]+_.*$/
const CATEGORY_FIELD_PREFIX = /^CATEGORY_[0-9]+_/

const toLowerStart = (s: string) => startCase(s.toLowerCase())

/**
 * Given points, extract the x and y offset
 */
export const getOffsetFromPoints = (
  points: CellPlotDatum
): { xOffset: number; yOffset: number } => {
  const { x, originalX, y, originalY } = points

  let xOffset = 0
  let yOffset = 0
  if (typeof originalX === 'number' && typeof originalY === 'number') {
    xOffset = x - originalX
    yOffset = originalY - y
  }

  return { xOffset, yOffset }
}

/**
 * Return the selection but remapped with the provided x and y offset
 * * This is used when taking a selection that the user drew in multiplot mode and
 *   figuring out where that selection WOULD have been in single plot mode
 */
export const getOriginalSelectionRange = (
  selectionRange: SelectionRange | undefined,
  xOffset: number,
  yOffset: number
): SelectionRange | undefined => {
  let offsetSelectionRange = selectionRange

  if (selectionRange) {
    offsetSelectionRange = {
      x: selectionRange.x.map((x) => x - xOffset),
      y: selectionRange.y.map((y) => y + yOffset),
    }
  }

  return offsetSelectionRange
}

/**
 * Ensures a lasso path string is properly closed by adding a straight line back to the start point if needed
 * @param path The SVG path string to check and close if necessary
 * @returns A properly closed path string
 */
export const ensurePathClosed = (path: string): string => {
  if (!path || typeof path !== 'string') return path
  
  if (path.endsWith('Z')) return path
  
  const coords = path.match(/-?[\d.]+/g)
  if (!coords || coords.length < 4) return path 
  
  const startX = coords[0]
  const startY = coords[1]
  
  const endX = coords[coords.length - 2]
  const endY = coords[coords.length - 1]
  
  if (startX === endX && startY === endY) {
    return `${path}Z`
  }
  
  // Add a straight line back to the start point and close the path
  return `${path}L${startX},${startY}Z`
}

/**
 * Given a plotly lasso path string, return the string with the x and y coords offset
 * @param `path` is always a string with counter clockwise coordinates in the form _`M{x coord},{y coord}L{x coord},{y coord}L ... {x coord},{y coord}Z`_
 * @example _`"M82.88815309825493,19.039055600379367L80.37256315136571,16.523465653490142L79.86944516198787,10.234490786267077L82.88815309825493,8.222018828755697L85.15218405045523,8.47357782344462L85.15218405045523,8.976695812822465Z"`_
 */
export const getOriginalPath = (path: string, xOffset: number, yOffset: number): string => {
  const coords = path.match(/[\d.]+/g)
  const newCoords: string[] = []

  if (coords) {
    for (let i = 0; i < coords?.length; i += 2) {
      const x = parseFloat(coords[i]) - xOffset
      const y = parseFloat(coords[i + 1]) + yOffset
      newCoords.push(`${x},${y}`)
    }
  }

  return `M${newCoords?.join('L')}Z`
}

/**
 * Given a plotly shape, return a new shape but with the x and y coordinates of all defined properties shifted based on the provided x and y offset
 * @param shape
 * @param xOffset
 * @param yOffset
 * @returns
 */
export const getOriginalShape = (
  shape: Partial<Shape>,
  xOffset: number,
  yOffset: number
): Partial<Shape> => {
  const { x0: oldx0, y0: oldy0, x1: oldx1, y1: oldy1, path: oldPath } = shape

  let x0 = oldx0
  let y0 = oldy0
  let x1 = oldx1
  let y1 = oldy1
  let path = oldPath

  // it's a range shape
  if (
    typeof oldx0 === 'number' &&
    typeof oldx1 === 'number' &&
    typeof oldy0 === 'number' &&
    typeof oldy1 === 'number'
  ) {
    x0 = oldx0 - xOffset
    y0 = oldy0 + yOffset
    x1 = oldx1 - xOffset
    y1 = oldy1 + yOffset
  }

  // it's a lasso shape
  if (oldPath) {
    path = getOriginalPath(oldPath, xOffset, yOffset)
  }

  return { type: shape.type, x0, y0, x1, y1, path }
}

/**
 * Given a PlotSelectionState, return a new PlotSelectionState with what the coordinates would be if the selection was made in single plot mode
 */
export const getOriginalPlotSelectionState = (
  selection: PlotSelectionState
): PlotSelectionState => {
  if (selection.points) {
    const { xOffset, yOffset } = getOffsetFromPoints(selection.points[0])
    const originalLassoPoints = getOriginalSelectionRange(selection.lassoPoints, xOffset, yOffset)
    const originalRange = getOriginalSelectionRange(selection.range, xOffset, yOffset)

    return {
      ...selection,
      lassoPoints: originalLassoPoints,
      range: originalRange,
      points: selection.points?.map((point) => ({
        x: point.originalX ?? 0,
        y: point.originalY ?? 0,
        cellId: point.cellId,
      })),
      selections: selection.selections?.map((s) => getOriginalShape(s, xOffset, yOffset)),
    }
  }

  return selection
}

/** Sorts values based on configuration of a CellDataField */
export const sortValuesByField = <T>(values: T[], field: CellDataField): T[] => {
  if (field.isContinuous || field.isNumerical) {
    return values.sort((a, b) => {
      const aNum = parseFloat(a as unknown as string)
      const bNum = parseFloat(b as unknown as string)
      return aNum - bNum
    })
  }
  return values.sort()
}

/**
 * Give a list of column names, return the custom fields as a list of CellDataFields
 */
export const getCustomFields = (fields: string[]): CellDataField[] =>
  fields.flatMap((field) =>
    isCategoryField.test(field)
      ? ({
          isContinuous: false,
          label: toLowerStart(field.replace(CATEGORY_FIELD_PREFIX, '').replace('_', ' ')),
          attribute: field,
          category: toLowerStart(FeatureGroup.CUSTOM),
        } as CellDataField)
      : []
  )

export const getHiddenCellInfoGroupValues = (
  cellInfoGroups: CellInfoGroups
): { [K in keyof CellInfoGroups]: string[] } => {
  return keys(cellInfoGroups).reduce((acc, cigk) => {
    const attribute = cigk as keyof typeof cellInfoGroups

    if (!attribute) return acc

    const { isContinuous, data } = cellInfoGroups[attribute] ?? {}

    if (isContinuous || !data) return acc

    const hiddenColValues = data.flatMap((x) => (x.isHidden ? x.value : []))

    if (hiddenColValues.length) {
      return { ...acc, [attribute]: hiddenColValues }
    }

    return acc
  }, {} as { [K in keyof typeof cellInfoGroups]: string[] })
}

export const removeDuplicatesAndSort = (cellInfo: CellInfoGroups): CellInfoGroups => {
  const cellInfoData = cellInfo
  keys(cellInfoData).forEach((_key) => {
    const key = _key as keyof CellInfoGroups
    if (cellInfo[key]) {
      const removeDuplicates = uniqBy(cellInfo[key]?.data, 'value')
      // eslint-disable-next-line no-param-reassign
      cellInfoData[key] = {
        ...cellInfoData[key],
        data: sortBy(removeDuplicates, (item) => {
          const { value } = item
          return /\d/.test(value) ? parseFloat(value) : value
        }),
      } as CellInfoGroup & typeof CellInfoGroupNone
    }
  })

  return cellInfoData
}

export const updateColors = (cellInfo: CellInfoGroups): CellInfoGroups => {
  keys(cellInfo).forEach((_key) => {
    const key = _key as keyof CellInfoGroups
    if (cellInfo[key]) {
      const data = cellInfo[key]?.data
      const updatedData = []
      if (data) {
        for (let i = 0; i < data.length; i += 1) {
          const modifiedCOlor = COLORS[i % COLORS.length]
          updatedData.push({
            ...data[i],
            color: modifiedCOlor,
          })
        }
      }
      // eslint-disable-next-line no-param-reassign
      cellInfo[key] = {
        ...cellInfo[key],
        data: updatedData,
      } as CellInfoGroup & typeof CellInfoGroupNone
    }
  })
  return cellInfo
}

export const filterObjectsWithNoData = (cellInfo: CellInfoGroups): CellInfoGroups => {
  const filteredEntries = Object.entries(cellInfo).filter(([_key, value]) => value.hasData)
  const filteredObject = Object.fromEntries(filteredEntries)

  return filteredObject
}

/**
 * Extract unique values for each field from cell data to create filter groups.
 * Memory-efficient: operates directly on DataFrame columns instead of creating object arrays
 */
export const getCellInfoToGroupsFromDataFrame = (
  dataFrame: ColumnTable,
  existingGroups?: CellInfoGroups,
  prefilteredCellIdSet?: Set<string>,
  isPreFilter?: boolean
): CellInfoGroups => {
  const cellInfoDataLength = dataFrame.numRows()

  if (cellInfoDataLength < 1) return {}

  // Get column names directly from DataFrame (more efficient than slicing)
  const columnNames = dataFrame.columnNames()
  if (!columnNames || columnNames.length === 0) return {}

  const dataFields = [...getDataFields(), ...getCustomFields(columnNames)]

  // Get CELL_ID column for filtering (if needed)
  const shouldFilter = !isPreFilter && prefilteredCellIdSet && prefilteredCellIdSet.size > 0
  const cellIdColumn = shouldFilter ? getColumn<string>(dataFrame, 'CELL_ID') : []

  const initializeCellInfoGroups = (cellDataFields: CellDataField[]): CellInfoGroups => {
    const initialCellInfoGroup: CellInfoGroups = {}
    for (let i = 0; i < cellDataFields.length; i += 1) {
      const { attribute, ...rest } = cellDataFields[i]
      initialCellInfoGroup[attribute as keyof CellInfo] = {
        hasData: false,
        data: [],
        ...rest,
      }
    }

    return initialCellInfoGroup
  }

  const cellInfoGroups: CellInfoGroups = initializeCellInfoGroups(dataFields)

  // OPTIMIZATION: Track unique values using Sets instead of pushing duplicates
  const uniqueValueSets = new Map<keyof CellInfo, Set<string>>()

  // OPTIMIZATION: Use Arquero's built-in aggregation to extract unique values directly
  // This is like SQL "SELECT DISTINCT column FROM table" - never materializes full column arrays
  // Benefits: ~1000x less memory (extracts only unique values, not all 100k-300k cells)
  // Memory: ~1 KB per field instead of ~800 KB per field
  // Scalability: Handles 300k cells with ~1.2 GB peak (vs 6+ GB with array extraction)

  dataFields.forEach(field => {
    const { attribute, isContinuous } = field
    const column = attribute as keyof CellInfo

    try {
      // Check if column exists in DataFrame
      const columnObj = dataFrame.column(attribute)
      if (!columnObj) return

      const cellInfoGroup = cellInfoGroups[column]
      if (!cellInfoGroup) return

      // Mark that we have data for this field
      cellInfoGroup.hasData = true

      // Only extract unique values for categorical variables (not continuous)
      if (!isContinuous) {

        // Handle both Arrow-based DataFrames and regular Arquero tables
        const col = columnObj
        const valueSet = new Set<string>()

        // For Arrow-based DataFrames: column.toArray() returns Arrow vector
        // For regular Arquero tables: column.toArray() doesn't exist, use DataFrame.array()
        if (col.toArray) {
          const arrowVector = col.toArray()

          // Type guard for Arrow vector with .get() method
          const isArrowVector = (v: unknown): v is { get: (index: number) => unknown; length: number } =>
            v !== null && typeof v === 'object' && 'get' in v && typeof (v as { get: unknown }).get === 'function' && 'length' in v

          if (isArrowVector(arrowVector)) {
            // Arrow vector with .get() method
            const { length } = arrowVector
            for (let i = 0; i < length; i += 1) {
              // Only include non-prefiltered cells in main visualization mode
              const isIncluded = !shouldFilter || !prefilteredCellIdSet?.has(cellIdColumn[i])
              if (isIncluded) {
                const value = arrowVector.get(i)
                if (value !== undefined && value !== null && value !== '') {
                  valueSet.add(String(value))
                }
              }
            }
          } else {
            // Regular JavaScript array from Arrow
            const columnData = arrowVector as unknown[]
            for (let i = 0; i < columnData.length; i += 1) {
              // Only include non-prefiltered cells in main visualization mode
              const isIncluded = !shouldFilter || !prefilteredCellIdSet?.has(cellIdColumn[i])
              if (isIncluded) {
                const value = columnData[i]
                if (value !== undefined && value !== null && value !== '') {
                  valueSet.add(String(value))
                }
              }
            }
          }
        } else {
          // Regular Arquero table (not Arrow-based) - use DataFrame.array()
          const columnArray = dataFrame.array(attribute) as unknown[]
          for (let i = 0; i < columnArray.length; i += 1) {
            // Only include non-prefiltered cells in main visualization mode
            const isIncluded = !shouldFilter || !prefilteredCellIdSet?.has(cellIdColumn[i])
            if (isIncluded) {
              const value = columnArray[i]
              if (value !== undefined && value !== null && value !== '') {
                valueSet.add(String(value))
              }
            }
          }
        }

        uniqueValueSets.set(column, valueSet)
        // Only the small Set is kept in memory (~10-100 unique values)
      }
    } catch (error) {
      // Skip columns that don't exist or have errors
      console.warn(`Error processing column ${attribute}:`, error)
    }
  })

  // Second pass: convert Sets to data arrays
  uniqueValueSets.forEach((valueSet, column) => {
    const cellInfoGroup = cellInfoGroups[column]
    if (cellInfoGroup?.data) {
      valueSet.forEach(value => {
        // Check if this value was hidden in existing groups
        const wasHidden = existingGroups?.[column]?.data?.find(
          item => item.value === value
        )?.isHidden || false

        cellInfoGroup.data.push({
          value,
          isHidden: wasHidden,
          color: '',
        })
      })
    }
  })

  const filterAndSortGroups = removeDuplicatesAndSort(cellInfoGroups)
  const filteredCellInfoGroups = filterObjectsWithNoData(filterAndSortGroups)
  const updatedWithColor = updateColors(filteredCellInfoGroups)

  return updatedWithColor
}

export default getCellInfoToGroupsFromDataFrame

type SimpleValue = string | number | boolean | null | undefined
/**
 * Return the first Value whose test asserts true, otherwise returns undefined
 */
export const coalesce = (
  ...values: ([boolean | undefined, SimpleValue] | SimpleValue)[]
): SimpleValue => {
  for (let i = 0; i < values.length; i += 1) {
    const [test, value] = Array.isArray(values[i])
      ? (values[i] as [boolean, SimpleValue])
      : [!!values[i] || values[i] === '', values[i] as SimpleValue]

    if (test) {
      return value
    }
  }

  return undefined
}
type ObjectCoords = { x: number[]; y: number[] }
type Coord = { x: number; y: number }
/**
 * Given an object with x and y coordinate arrays, remove the coordinates that are in
 * an array that contains an object with x,y coordinate properties.
 * Should be very fast as the Set.has() function has a time complexity of nearly O(1)
 * @param objectCoords An object that can extend { x: number[]; y: number[] }
 * @param arrayOfCoords An array that contains objects that can extend { x: number; y: number }
 * @returns A new object with data from the original object but with the x and y arrays filtered
 */

export const removeArrCoordsFromObjectCoords = <OC extends ObjectCoords, C extends Coord>(
  objectCoords: OC,
  arrayOfCoords: C[]
): OC => {
  const coordSet = new Set<string>(arrayOfCoords.map((coord) => `${coord.x}${coord.y}`))

  return {
    ...objectCoords,
    x: objectCoords.x.filter((x, i) => !coordSet.has(`${x}${objectCoords.y[i]}`)),
    y: objectCoords.y.filter((y, i) => !coordSet.has(`${objectCoords.x[i]}${y}`)),
  }
}

export const removeObjectCoordsFromArrCoords = <OC extends ObjectCoords, C extends Coord>(
  objectCoords: OC,
  arrayOfCoords: C[]
): C[] => {
  const coordSet = new Set<string>(objectCoords.x.map((x, i) => `${x}${objectCoords.y[i]}`))

  return arrayOfCoords.filter((aoc) => !coordSet.has(`${aoc.x}${aoc.y}`))
}

/** Utility function to check for invalid intersections
 *
 * Due to the nature of using a mouse, we saw that in all cell vis sessions
 * created up until May 2024 that had morphotypes, roughly a quarter of those
 * sessios had morphotype polygon regions with self-intersections.
 *
 * The vast majority of those self-intersections looked like tiny blips
 * due to random mouse motion (probably often while making corners or closing
 * the polygon)
 *
 * This function detects if the selection has self-intersections, but
 * ignores cases where those intersections look relatively tiny.
 *
 * The backend will try to resolve those cases in a way that matches
 * what Plotly shows (but the areas are so tiny it probably doesn't matter)
 *
 * @param lassoPoints The lasso selection points to check and repair
 * @param maxAreaThreshold The maximum area threshold for a hole to be considered too large
 *                     taking the area of the region to remove vs. the total area selected
 *
 * @returns true if the selection if the lasso selection has no self-intersections
 *    or only "small" self-intersections
 */
export const isSelectionValid = (lassoPoints: SelectionRange, maxAreaThreshold = 1e-2): boolean => {
  const coords = zip(lassoPoints.x, lassoPoints.y) as unknown as turf.Position[]

  // turf expects the first and last point to be the same
  // so let's close the loop
  const p0 = coords[0]
  const pn = coords[coords.length - 1]
  if (p0[0] !== pn[0] || p0[1] !== pn[1]) {
    coords.push(coords[0])
  }

  // Create a GeoJSON turf polygon from the selection
  const inputPoly = turf.polygon([coords])

  // Unkinks the polygon by removing all self-intersections
  // And returning instead a list of simple polygons that may have one or more interior holes
  // https://www.npmjs.com/package/simplepolygon
  const unkinkedPoly = unkinkPolygon(inputPoly)

  // Calculate the total area of the selection
  // (turf does this in square meters...so the units are a bit funky)
  const totalArea = area(unkinkedPoly)

  // Check for any large holes or large extra polygons
  let hasLargeHoles = false
  let largePolygonCount = 0
  unkinkedPoly.features.forEach((poly: turf.Feature<turf.Polygon>) => {
    const exteriorRing = poly.geometry.coordinates[0]
    const interiorRings = poly.geometry.coordinates.slice(1)

    // Keep interior holes that are larger than the threshold
    const filteredInteriorRings = interiorRings.filter(
      (ring) => area(turf.polygon([ring])) / totalArea > maxAreaThreshold
    )

    // If we have large holes, this selection is invalid
    if (filteredInteriorRings.length > 0) {
      hasLargeHoles = true
      return
    }

    // Count how many large polygons we have after unkinking
    if (area(turf.polygon([exteriorRing])) / totalArea > maxAreaThreshold) {
      largePolygonCount += 1
    }
  })

  // If there's multiple large polygons or large holes, the selection is invalid
  return largePolygonCount === 1 && !hasLargeHoles
}

/**
 * Ensure a polygon ring is properly closed (first and last coordinates are the same)
 * Required by turf.polygon which validates that rings are closed
 * @param coords Array of turf positions
 * @returns Array of positions with the ring properly closed
 */
const ensureClosedRing = (coords: turf.Position[]): turf.Position[] => {
  if (coords.length === 0) return coords

  const first = coords[0]
  const last = coords[coords.length - 1]

  // Check if first and last positions are the same
  if (first[0] === last[0] && first[1] === last[1]) {
    return coords
  }

  // Close the ring by adding the first position at the end
  return [...coords, first]
}

/**
 * Simple bounding box calculation for overlap detection
 */
const getBoundingBox = (coords: turf.Position[]) => {
  const xs = coords.map(p => p[0])
  const ys = coords.map(p => p[1])
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys)
  }
}

/**
 * Check if two bounding boxes overlap
 */
const boxesOverlap = (box1: ReturnType<typeof getBoundingBox>, box2: ReturnType<typeof getBoundingBox>) => {
  return !(box1.maxX < box2.minX || box1.minX > box2.maxX || box1.maxY < box2.minY || box1.minY > box2.maxY)
}

/**
 * Check if two cluster-based morphotypes share any cells
 * @param morphotype1 First cluster-based morphotype
 * @param morphotype2 Second cluster-based morphotype
 * @returns True if they share at least one cell
 */
const checkClusterOverlapWithCluster = (
  morphotype1: PinnedMorphotype,
  morphotype2: PinnedMorphotype
): boolean => {
  const cells1 = new Set(morphotype1.cells.points?.map(p => p.cellId).filter(Boolean))
  const cells2 = new Set(morphotype2.cells.points?.map(p => p.cellId).filter(Boolean))

  // Check if any cell IDs are shared
  return Array.from(cells1).some(cellId => cells2.has(cellId))
}

/**
 * Check if any points from a cluster-based morphotype fall within a geometric polygon
 * @param clusterMorphotype Cluster-based morphotype with discrete points
 * @param geometricMorphotype Geometric morphotype with lasso selection
 * @returns True if any cluster points are inside the polygon
 */
const checkClusterOverlapWithGeometric = (
  clusterMorphotype: PinnedMorphotype,
  geometricMorphotype: PinnedMorphotype
): boolean => {
  if (!geometricMorphotype.cells.lassoPoints || !clusterMorphotype.cells.points) {
    return false
  }

  const polygonCoords = zip(
    geometricMorphotype.cells.lassoPoints.x,
    geometricMorphotype.cells.lassoPoints.y
  ) as unknown as turf.Position[]

  // Ensure the polygon ring is closed (required by turf.polygon)
  const closedCoords = ensureClosedRing(polygonCoords)
  const polygon = turf.polygon([closedCoords.map(coord => [coord[0], coord[1]])])

  // Check if any cluster point is inside the polygon
  return clusterMorphotype.cells.points.some(point => {
    const cellPoint = turf.point([point.x, point.y])
    return booleanPointInPolygon(cellPoint, polygon)
  })
}

/**
 * Check if a new lasso selection overlaps with existing morphotype selections
 * @param newLassoPoints The new lasso selection points to check
 * @param existingMorphotypes Array of existing pinned morphotypes
 * @returns Object containing overlap information
 */
export const checkForOverlappingSelections = (
  newLassoPoints: SelectionRange,
  existingMorphotypes: PinnedMorphotype[]
): {
  hasOverlap: boolean
  overlappingMorphotypes: PinnedMorphotype[]
} => {
  if (!existingMorphotypes || existingMorphotypes.length === 0) {
    return {
      hasOverlap: false,
      overlappingMorphotypes: [],
    }
  }

  // Convert new lasso points to coordinates
  const newCoords = zip(newLassoPoints.x, newLassoPoints.y) as unknown as turf.Position[]
  const newBounds = getBoundingBox(newCoords)

  // Ensure the polygon ring is closed (required by turf.polygon)
  const closedNewCoords = ensureClosedRing(newCoords)
  const newPolygon = turf.polygon([closedNewCoords.map(coord => [coord[0], coord[1]])])

  const overlappingMorphotypes: PinnedMorphotype[] = []

  existingMorphotypes.forEach((morphotype) => {
    // Check geometric (lasso) morphotypes
    if (morphotype.cells.lassoPoints) {
      const existingCoords = zip(morphotype.cells.lassoPoints.x, morphotype.cells.lassoPoints.y) as unknown as turf.Position[]
      const existingBounds = getBoundingBox(existingCoords)

      // First check bounding box for quick rejection
      if (boxesOverlap(newBounds, existingBounds)) {
        // Then check actual polygon intersection for accurate detection
        try {
          const newPolygonClipping: Poly = [newCoords.map(coord => [coord[0], coord[1]])]
          const existingPolygon: Poly = [existingCoords.map(coord => [coord[0], coord[1]])]

          const intersectionResult = intersection(newPolygonClipping, existingPolygon)

          // If there's an intersection, the polygons overlap
          if (intersectionResult && intersectionResult.length > 0) {
            overlappingMorphotypes.push(morphotype)
          }
        } catch (error) {
          // If polygon intersection fails, fall back to bounding box check
          console.warn('Polygon intersection failed, falling back to bounding box check:', error)
          overlappingMorphotypes.push(morphotype)
        }
      }
    }
    // Check cluster-based morphotypes
    else if (morphotype.isClusterBased && morphotype.cells.points) {
      // Check if any cluster points are inside the new polygon
      const hasPointInPolygon = morphotype.cells.points.some(point => {
        const cellPoint = turf.point([point.x, point.y])
        return booleanPointInPolygon(cellPoint, newPolygon)
      })
      if (hasPointInPolygon) {
        overlappingMorphotypes.push(morphotype)
      }
    }
  })

  return {
    hasOverlap: overlappingMorphotypes.length > 0,
    overlappingMorphotypes,
  }
}

/**
 * Check if there are overlaps between all active pinned morphotypes.
 * Handles overlaps between:
 * - Geometric morphotypes (lasso selections)
 * - Cluster-based morphotypes (discrete point sets)
 * - Mixed overlaps (geometric vs cluster-based)
 *
 * Used to validate before training classifiers
 * @param morphotypes Array of pinned morphotypes to check
 * @returns Array of overlapping morphotype pairs
 */
export const checkForOverlappingMorphotypes = (
  morphotypes: PinnedMorphotype[]
): { morphotype1: PinnedMorphotype; morphotype2: PinnedMorphotype }[] => {
  if (!morphotypes || morphotypes.length < 2) {
    return []
  }

  // Only check active morphotypes (both geometric and cluster-based)
  const activeMorphotypes = morphotypes.filter(
    m => m.active !== false && (m.cells?.lassoPoints || (m.isClusterBased && m.cells?.points))
  )

  if (activeMorphotypes.length < 2) {
    return []
  }

  const overlappingPairs: { morphotype1: PinnedMorphotype; morphotype2: PinnedMorphotype }[] = []

  // Check each pair of morphotypes for overlaps
  for (let i = 0; i < activeMorphotypes.length; i += 1) {
    for (let j = i + 1; j < activeMorphotypes.length; j += 1) {
      const morphotype1 = activeMorphotypes[i]
      const morphotype2 = activeMorphotypes[j]

      let hasOverlap = false

      // Case 1: Both are geometric (lasso-based)
      if (morphotype1.cells?.lassoPoints && morphotype2.cells?.lassoPoints) {
        const overlapResult = checkForOverlappingSelections(
          morphotype1.cells.lassoPoints,
          [morphotype2]
        )
        hasOverlap = overlapResult.hasOverlap
      }
      // Case 2: Both are cluster-based
      else if (morphotype1.isClusterBased && morphotype2.isClusterBased) {
        hasOverlap = checkClusterOverlapWithCluster(morphotype1, morphotype2)
      }
      // Case 3: One is cluster-based, one is geometric
      else if (morphotype1.isClusterBased && morphotype2.cells?.lassoPoints) {
        hasOverlap = checkClusterOverlapWithGeometric(morphotype1, morphotype2)
      } else if (morphotype2.isClusterBased && morphotype1.cells?.lassoPoints) {
        hasOverlap = checkClusterOverlapWithGeometric(morphotype2, morphotype1)
      }

      if (hasOverlap) {
        overlappingPairs.push({ morphotype1, morphotype2 })
      }
    }
  }

  return overlappingPairs
}

/**
 * Combines and deduplicates points from pinnedCells and mergedPinnedCells for given morphotype IDs.
 * This is used when merging morphotypes or reconstructing merged morphotype points.
 * 
 * @param pinnedCells - Array of pinned morphotypes
 * @param mergedPinnedCells - Array of merged morphotypes (may be undefined)
 * @param idsToMerge - Single ID or array of IDs to combine points for
 * @param options - Optional configuration
 * @param options.shuffle - Whether to shuffle the final points array (default: false)
 * @returns Array of deduplicated points
 */
export function combineMorphotypePoints(
  pinnedCells: PinnedMorphotype[] | undefined,
  mergedPinnedCells: Array<{ id: number; cells: PlotSelectionState }> | undefined,
  idsToMerge: number | number[],
  options: { shuffle?: boolean } = {}
): CellPlotDatum[] {
  const { shuffle: shouldShuffle = false } = options
  const idsArray = Array.isArray(idsToMerge) ? idsToMerge : [idsToMerge]

  // Collect points from pinnedCells
  const pointsFromPinnedCells =
    pinnedCells
      ?.filter((x) => idsArray.includes(x.id))
      .flatMap((x) => {
        // For multi-plot morphotypes, prefer transposedCells as it has the complete tiled-space data
        // For single-plot or migrated morphotypes, use cells
        const pointsSource = x.transposedCells ?? x.cells
        return pointsSource.points ?? []
      }) ?? []

  // Collect points from mergedPinnedCells
  const pointsFromMergedCells =
    mergedPinnedCells
      ?.filter((x) => idsArray.includes(x.id))
      .flatMap((x) => x.cells.points ?? []) ?? []

  // Combine all points
  const allPoints = [...pointsFromPinnedCells, ...pointsFromMergedCells]

  // Deduplicate by cellId to handle cells selected in different subplots
  // (same cell can have different x,y offsets in multi-plot mode)
  // Fallback to original coordinates if cellId is somehow missing
  const uniquePoints = uniqBy(
    allPoints,
    (p) => p.cellId || `${p.originalX ?? p.x}_${p.originalY ?? p.y}`
  )

  // Optionally shuffle the points
  return shouldShuffle ? shuffle(uniquePoints) : uniquePoints
}
